import { Divider, Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { Alert, api, getQueryProps } from 'common';

import { MirthMessageType, mirthDefaultMessages } from './constants';
import { useFlowDefinitionContext } from '../../../../context';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../ConfigFields/Duration';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const { DurationUnits } = api.FlowDefinition;

export function MirthFormFields() {
  const { flowDefinition } = useFlowDefinitionContext();
  const { organizationId } = flowDefinition;

  const { value: messageType } = useSelectedNodeField<string>('config.messageType');
  const { setValue: setMessage } = useSelectedNodeField<string>('config.message');

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    ...getQueryProps(api.client.Organization, 'findOneById', organizationId),
  });

  if (isLoading) {
    return <Skeleton />;
  }
  if (error || !organization) {
    return (
      <Alert variant="warning">
        Interface engine exports are not enabled for this practice and organization.
      </Alert>
    );
  }

  return (
    <>
      <Stack direction="column" spacing={2}>
        <BlockSelectField
          fieldName="config.messageType"
          label="Message Type"
          helperText="Message type to send"
          options={Object.values(MirthMessageType).map((value) => ({
            label: value.toUpperCase(),
            value,
          }))}
          required
          onChange={(event) => {
            setMessage(
              mirthDefaultMessages[event.target.value as keyof typeof mirthDefaultMessages] || ''
            );
          }}
        />
        <BlockValueTemplateField
          fieldName="config.path"
          label="Interface Engine Path"
          helperText="Interface engine path to send the message to"
        />
        <Divider />

        <BlockValueTemplateFieldArea
          fieldName="config.message"
          label={messageType === MirthMessageType.Json ? 'Body' : 'Message'}
          helperText={
            messageType === MirthMessageType.Json
              ? 'Enter the JSON body'
              : 'Enter the message content'
          }
          required
        />
        <BlockDurationField
          variant="BlockValueTemplate"
          fieldName="config.timeout"
          required={true}
          unitFieldNameSuffix="units"
          allowedUnits={[DurationUnits.Seconds, DurationUnits.Minutes]}
          toggleLabel="Timeout"
          valueLabel="Timeout"
          valueHelperText="Maximum duration to wait for a response from the Interface Engine before retrying"
        />
      </Stack>
    </>
  );
}
